/**
 * LeftSidebar — 데스크탑 전용 좌측 사이드바 (국가/도시/카테고리)
 * 모바일은 MainMenu 가 가로 바 형태로 담당, 데스크탑은 본 컴포넌트가 세로 배치
 */

import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { LayoutGrid } from "lucide-react";
import { useCountries, useCities, useCategories } from "../../hooks/useBusinessData";
import MiniDropdown from "../ui/MiniDropdown";
import { categoryButtonClass, CATEGORY_TOOLTIP } from "../../constants/categoryStyles";
import CategoryIcon from "../ui/CategoryIcon";

interface LeftSidebarProps {
  activeCategoryKey: string | null;
  activeCountryKey: string;
  activeCityKey: string | null;
  onCategoryChange: (key: string | null, id?: number | null) => void;
  onCountryChange: (key: string, id: number) => void;
  onCityChange: (key: string | null, id?: number | null) => void;
}

interface TooltipState {
  text: string;
  left: number;
  top: number;
}

export default function LeftSidebar({
  activeCategoryKey,
  activeCountryKey,
  activeCityKey,
  onCategoryChange,
  onCountryChange,
  onCityChange,
}: LeftSidebarProps) {
  const { t } = useTranslation();
  const dbCountries = useCountries();
  const dbCities = useCities(activeCountryKey);
  const dbCategories = useCategories();
  const cityOptions = [
    { key: "__all__", label: t("menu.allCities") },
    ...dbCities.map((c) => ({ key: c.key, label: c.label })),
  ];

  // hover 툴팁 (카테고리 description)
  const [tip, setTip] = useState<TooltipState | null>(null);
  const handleEnter = (description: string | undefined, e: React.MouseEvent<HTMLButtonElement>) => {
    if (!description) return;
    const r = e.currentTarget.getBoundingClientRect();
    setTip({
      text: description,
      left: Math.round(r.right + 8),
      top: Math.round(r.top + r.height / 2),
    });
  };
  const handleLeave = () => setTip(null);

  return (
    <aside
      className="hidden md:flex shrink-0 flex-col gap-3 w-[200px] border-r border-slate-200 bg-white px-3 py-3 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      aria-label="filters"
    >
      {/* 국가 — 요구사항 #552: 필리핀 고정(선택 불가). 드롭다운 대신 고정 라벨. */}
      <div className="w-full flex items-center gap-1.5 px-1 py-0.5 text-sm text-slate-900">
        {(() => {
          const ph = dbCountries.find((c) => c.key === "ph");
          return (<>
            <span className="shrink-0">{ph?.flag || "🇵🇭"}</span>
            <span className="truncate">{ph?.label || "Philippines"}</span>
          </>);
        })()}
      </div>

      {/* 도시 — 요구사항 #293: 아래 화살표 표시 */}
      <MiniDropdown
        theme="light"
        showChevron
        value={activeCityKey ?? "__all__"}
        options={cityOptions}
        onChange={(key) => {
          if (key === "__all__") {
            onCityChange(null, null);
            return;
          }
          const c = dbCities.find((x) => x.key === key);
          onCityChange(key, c?.id ?? null);
        }}
        className="w-full"
      />

      {/* 카테고리 — 세로 버튼 목록 (라벨 좌측에 아이콘 + 라벨, 인식 가능한 크기) */}
      <div className="flex flex-col gap-0.5 mt-1">
        <button
          onClick={() => onCategoryChange(null, null)}
          className={`flex items-center text-left w-full rounded-md transition-colors ${categoryButtonClass(activeCategoryKey === null)}`}
        >
          <span className="inline-flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-white shadow-sm shrink-0 text-slate-600">
              <LayoutGrid className="h-4 w-4" strokeWidth={2.2} />
            </span>
            <span>{t("menu.allCategories")}</span>
          </span>
        </button>
        {dbCategories.map((cat) => (
          <div key={cat.key} className={CATEGORY_TOOLTIP.wrapper}>
            <button
              onClick={() => onCategoryChange(cat.key, cat.id)}
              onMouseEnter={(e) => handleEnter(cat.description, e)}
              onMouseLeave={handleLeave}
              className={`flex items-center text-left w-full rounded-md transition-colors ${categoryButtonClass(activeCategoryKey === cat.key)}`}
            >
              <span className="inline-flex items-center gap-2">
                <CategoryIcon cat={cat} size="md" />
                <span>{cat.label}</span>
              </span>
            </button>
          </div>
        ))}
      </div>

      {/* hover 툴팁 — Portal body 직접 렌더, 사이드바 우측에 표시 */}
      {tip &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-[9999] -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-900/95 px-2 py-1 text-[11px] text-white shadow-lg"
            style={{ left: tip.left, top: tip.top }}
          >
            {tip.text}
          </div>,
          document.body,
        )}
    </aside>
  );
}
